import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as ngrxComponentStore from '@ngrx/component-store'
import * as rxjs from 'rxjs'

import * as state from '../../../../../../state'

const DEFAULT_ROOT_CONNECTOR = ents.extra.rate.gl.Filter.Statement.Connector.And

export interface AtnGlFilterState {
  readonly rootFilter: ents.extra.rate.gl.Filter.ExplicitlyTyped
  readonly operators: readonly ents.extra.rate.gl.Filter.Comparison.Operator[]
}
export namespace AtnGlFilterState {
  export const INITIAL: AtnGlFilterState = {
    operators: ents.extra.rate.gl.Filter.Comparison.Operator.ALL,
    rootFilter: {},
  }
}

@ngCore.Injectable()
export class AtnGlFilterStore extends ngrxComponentStore.ComponentStore<AtnGlFilterState> {
  /**
   * @see {@link state.rate.gl.records.Collection}
   */
  public readonly glRecordEc = ngCore.inject(state.rate.gl.records.Collection)

  /**
   * ID for the root {@link ents.extra.rate.gl.Filter}
   */
  public readonly rootId = this.createId()

  /**
   * Root {@link ents.extra.rate.gl.Filter} being rendered
   */
  public readonly rootFilterSg = this.selectSignal(_state => _state.rootFilter)

  /**
   * Used to emit direct changes to {@link rootFilterSg}
   */
  public readonly rootFilterChange$ =
    new rxjs.Subject<ents.extra.rate.gl.Filter.ExplicitlyTyped>()

  /**
   * Selectable {@link ec.models.Attribute}
   */
  public attributesSg =
    this.glRecordEc.plugins.attributePlugin.feature.selectors.attributes.project(
      attributes =>
        // Only DMS attributes are eligible for filters
        attributes.filter(attribute =>
          ents.core.dms.models.Attribute.Id.isId(attribute.id),
        ),
    ).asSignal

  /**
   * List of {@link ds.input.select.models.Item} corresponding to {@link attributesSg}
   */
  public attributeItemsSg = ngCore.computed(
    (attributes = this.attributesSg()) =>
      attributes.map<
        ds.input.select.models.Item<ents.core.common.Attribute.Id>
      >(attribute => ({
        label: attribute.name,
        value: attribute.id,
      })),
  )

  /**
   * Selectable {@link ents.extra.rate.gl.Filter.Comparison.Operator}
   */
  public operatorsSg = this.selectSignal(_state => _state.operators)

  /**
   * List of {@link ds.input.select.models.Item} corresponding to {@link operatorsSg}
   */
  public readonly operatorItemsSg = ngCore.computed(
    (operators = this.operatorsSg()) =>
      operators.map<
        ds.input.select.models.Item<ents.extra.rate.gl.Filter.Comparison.Operator>
      >(operator => ({
        label: operator,
        value: operator,
      })),
  )

  /**
   * @see {@link DEFAULT_ROOT_CONNECTOR}
   */
  public readonly defaultRootConnector = DEFAULT_ROOT_CONNECTOR

  /**
   * Default {@link ents.extra.rate.gl.Filter}
   */
  public readonly defaultAttributeIdSg = ngCore.computed(
    (attributes = this.attributesSg()) => attributes[0]?.id,
  )
  /**
   * Default {@link ents.extra.rate.gl.Filter}
   */
  public readonly defaultFilterSg = ngCore.computed<ents.extra.rate.gl.Filter>(
    (defaultAttributeId = this.defaultAttributeIdSg()) => ({
      // NOTE: if we don't have a valid attribute ID, we still want the user to
      // be able to edit the filter
      attributeId: (defaultAttributeId ?? undefined) satisfies
        | string
        | undefined as ents.core.dms.models.Attribute.Id,
      operator: ents.extra.rate.gl.Filter.Comparison.Operator.Equals,
      value: '',
    }),
  )

  constructor() {
    super(AtnGlFilterState.INITIAL)
  }

  /**
   * Creates a new ID for a {@link ents.extra.rate.gl.Filter}
   */
  public createId({
    index = 0,
    parentId,
  }: {
    readonly index?: number
    readonly parentId?: string
  } = {}): string {
    return parentId ? `${parentId}${ID_SEPARATOR}${index}` : index.toString()
  }

  /**
   * Creates a new ID for a filter
   */
  public getDepth(id: string | undefined): number {
    if (id === undefined) {
      // No ID = one before root. Since root is 0, this would be -1
      return -1
    }
    const parts = id.split(ID_SEPARATOR)
    // Each part represents a level. First level = depth of 0, so we always subtract 1
    return parts.length - 1
  }

  /**
   * Applies a change to the root filter, only if it is a
   * {@link ents.extra.rate.gl.Filter.Statement}
   */
  public changeRootStatement(
    change: (
      statement: ents.extra.rate.gl.Filter.Statement,
    ) => ents.extra.rate.gl.Filter.Statement,
  ): void {
    const { statement } = this.rootFilterSg()
    if (!statement) {
      return
    }
    this.rootFilterChange$.next({ statement: change(statement) })
  }

  /**
   * Inverts the connectors throughout the root filter
   *
   * @see {@link ents.extra.rate.gl.Filter.Statement.invertConnector}
   */
  public invertConnectors(): void {
    this.changeRootStatement(
      ents.extra.rate.gl.Filter.Statement.invertConnector,
    )
  }

  /**
   * Adds a sibling to the identified {@link ents.extra.rate.gl.Filter} in the
   * root filter
   */
  public addSiblingTo(id: string): void {
    const sibling = this.defaultFilterSg()
    if (id === this.rootId) {
      // Turns the root filter into a statement that contains the current root
      // filter and a new filter
      const currentRootFilter = ents.extra.rate.gl.Filter.from(
        this.rootFilterSg(),
      )
      this.rootFilterChange$.next({
        statement: {
          connector: DEFAULT_ROOT_CONNECTOR,
          filters: currentRootFilter ? [currentRootFilter, sibling] : [sibling],
        },
      })
    } else {
      // Adds a nested filter
      this.changeRootStatement(statement =>
        _modifyNestedStatement(statement, id, { add: sibling }),
      )
    }
  }

  /**
   * Removes the identified {@link ents.extra.rate.gl.Filter} from within the
   * root filter
   */
  public remove(id: string): void {
    if (id === this.rootId) {
      // Removes the root filter
      this.rootFilterChange$.next({})
    } else {
      // Removes a nested filter
      this.changeRootStatement(statement =>
        _modifyNestedStatement(statement, id, 'remove'),
      )
    }
  }
}

const ID_SEPARATOR = '°'

function _modifyNestedStatement(
  statement: ents.extra.rate.gl.Filter.Statement,
  id: string,
  action: 'remove' | { add: ents.extra.rate.gl.Filter },
): ents.extra.rate.gl.Filter.Statement {
  const newStatement = { ...statement }
  let newNestedStatement = newStatement
  const idParts = id.split(ID_SEPARATOR).slice(1)
  const lastIdPart = idParts.pop()
  for (const idPart of idParts) {
    const index = Number(idPart)
    const filter = newNestedStatement.filters[index]
    if (!ents.extra.rate.gl.Filter.Statement.isProbablyStatement(filter)) {
      // If the current filter is not a nested statement, there is nothing else
      // we can do
      return newStatement
    }
    // Creates copy so that we can modify the list of filters without affecting
    // the original statement
    newNestedStatement.filters = [...newNestedStatement.filters]
    // Then creates copy of and moves down into the next nested statement
    newNestedStatement.filters[index] = newNestedStatement = { ...filter }
  }
  const lastIndex = Number(lastIdPart)
  // Creates a shallow copy, to avoid overwriting the original statement, and
  // only includes filters up until the specified index
  const existingFilters = newNestedStatement.filters
  newNestedStatement.filters = existingFilters.slice(0, lastIndex)
  if (action !== 'remove') {
    // If we are removing, then we can skip this step because we no longer want
    // the specified index. Otherwise, we are adding, in which case we add back
    // the specified index and the new filter to add
    newNestedStatement.filters.push(existingFilters[lastIndex], action.add)
  }
  // Finally, we add all the filters after the specified index
  newNestedStatement.filters.push(...existingFilters.slice(lastIndex + 1))
  if (newNestedStatement.filters.length === 1) {
    // If there is only one filter, we might as well get rid of the statement
    // and just turn this statement into a single condition
    const onlyCondition = newNestedStatement.filters[0]
    // NOTE: this is a bit hacky:
    Object.assign(
      // We write to the existing "statement" and transform it into a condition
      newNestedStatement,
      // First by overwriting the existing properties and making them
      // `undefined`
      {
        connector: undefined,
        filters: undefined,
      } satisfies NoMissing<
        Replace.Properties.Entirely<
          ents.extra.rate.gl.Filter.Statement,
          undefined
        >
      >,
      // Then by writing in the properties of the only condition
      onlyCondition,
    )
  }
  return newStatement
}
